import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh, type PillTab } from '@ah/design/mobile';
import { Video } from 'lucide-react';
import { VIDEO_CHANNELS_QUERY, type ChannelListItem } from '../shared/index.ts';
import { ChannelGridSkeleton, ErrorRetry } from './_parts.tsx';

const TABS: PillTab[] = [
  { to: '/videos',          label: 'For You', end: true },
  { to: '/videos/latest',   label: 'Latest' },
  { to: '/videos/channels', label: 'Channels' },
  { to: '/videos/seen',     label: 'Watched' },
  { to: '/videos/saved',    label: 'Saved' },
];

export function VideoChannels() {
  const gql = useGql();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['videos', 'channels', 'list'],
    queryFn: async () => {
      const res = await gql.request<{ videoChannels: ChannelListItem[] }>(VIDEO_CHANNELS_QUERY);
      return res.videoChannels;
    },
    refetchInterval: 60_000,
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Videos" />
      <PillTabs tabs={TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={() => refetch()}>
        {isLoading ? (
          <ChannelGridSkeleton />
        ) : error ? (
          <ErrorRetry label="Could not load channels." onRetry={() => refetch()} />
        ) : !data || data.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Video size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No channels imported yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {data.map((c) => <ChannelCell key={c.id} c={c} />)}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}

function ChannelCell({ c }: { c: ChannelListItem }) {
  return (
    <Link to={`/videos/channel/${c.id}`} className="block space-y-2">
      <div className="aspect-square rounded-xl overflow-hidden bg-muted">
        {c.imageUrl ? (
          <img src={c.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground opacity-40">
            <Video size={28} />
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium leading-snug line-clamp-2">{c.title}</p>
        {c.handle ? <p className="text-xs text-muted-foreground line-clamp-1">{c.handle}</p> : null}
      </div>
    </Link>
  );
}
